export enum PushType {
  REGULAR = 'regular',
  EVENT = 'event',
  SPECIAL = 'special'
}

export enum ConfigType {
  SINGLE = 'single',
  CAMPAIGN = 'campaign'
}

export enum PushStatus {
  SCHEDULED = 'scheduled',
  SENDING = 'sending',
  SENT = 'sent',
  FAILED = 'failed',
  CANCELLED = 'cancelled' 
}

export interface PushMessage {
  id: number;
  message_id: string;
  app_id: number;
  push_type: PushType;
  config_type: ConfigType;
  campaign_id?: number;
  title: string;
  body: string;
  image_url?: string;
  click_url?: string; 
  custom_data?: Record<string, any>;
  segment_id?: number;
  target_users_count: number;
  status: PushStatus;
  scheduled_at?: string;
  sent_at?: string;
  created_at: string;
  updated_at: string;
  
  sent_count: number;
  delivered_count: number;
  clicked_count: number;
  segment_name?: string;
}

export interface SendPushRequest {
  app_id: number;
  title: string;
  body: string;
  image_url?: string;
  click_url?: string; 
  segment_id?: number;
  custom_data?: Record<string, any>;
  schedule_at?: string;
  push_type?: PushType;
}

export interface Campaign {
  id: number;
  app_id?: number;
  scope?: 'app' | 'global';
  target_app_ids?: number[];
  name: string;
  description: string;
  push_type: PushType;
  segment_id?: number;
  locale?: string;
  has_registration?: boolean;
  has_deposit?: boolean;
  os?: string;
  offer?: string;
  start_date: string;
  end_date?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  pushes?: CampaignPush[];
  pushes_count?: number;
}

export interface CampaignPush {
  id: number;
  campaign_id: number;
  sequence_order: number;
  delay_after_previous?: number;
  title: string;
  body: string;
  image_url?: string;
  click_url?: string;
  custom_data?: Record<string, any>;
  segment_id?: number;
  created_at: string;
}

export interface Segment {
  id: number;
  app_id?: number;
  scope?: 'app' | 'global';
  name: string;
  description: string;
  conditions: SegmentCondition[];
  user_count: number;
  created_at: string;
  updated_at: string;
}

export interface SegmentCondition {
  field: 'has_registration' | 'has_deposit' | 'locale' | 'country' | 'os' | 'offer' | 'install_date' | 'buyer_id' ;
  operator: 'equals' | 'greater_than' | 'less_than' | 'contains' | 'not_contains';
  value: any;
}

export interface PushStatistics {
  id: number;
  message_id: string;
  sent_count: number;
  delivered_count: number;
  clicked_count: number;
  failed_count: number;
  attributed_registrations: number;
  attributed_deposits: number;
  updated_at: string;
}

export interface PushHistoryFilter {
  app_id?: number;
  push_type?: PushType;
  status?: PushStatus;
  date_from?: string;
  date_to?: string;
  campaign_id?: number;
  search?: string;
  segment_id?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface CampaignCreateRequest {
  app_id: number;
  name: string;
  description: string;
  push_type: PushType;
  segment_id?: number;
  locale?: string;
  has_registration?: boolean;
  has_deposit?: boolean;
  os?: string;
  offer?: string;
  start_date: string;
  end_date?: string;
  is_active?: boolean;
  pushes: Omit<CampaignPush, 'id' | 'campaign_id' | 'created_at'>[];
}

export interface SegmentCreateRequest {
  app_id: number;
  name: string;
  description: string;
  conditions: SegmentCondition[];
}

export interface PushReport {
  message: PushMessage;
  statistics: PushStatistics;
  conversion_rate: number;
  ctr: number;
  delivery_rate: number;
}

export interface DailyPushStats {
  date: string;
  push_count: number;
  sent: number;
  delivered: number;
  clicked: number;
  registrations: number;
  deposits: number;
  ctr: number;
}

export interface CampaignPerformance {
  id: number;
  name: string;
  push_type: PushType;
  push_count: number;
  sent: number;
  clicked: number;
  registrations: number;
  deposits: number;
  ctr: number;
  conversion_rate: number;
}

export interface DashboardStats {
  users: {
    total_users: number;
    active_users: number;
    deleted_users: number;
    with_registration: number;
    with_deposit: number;
  };
  push_stats: {
    total_sent: number;
    total_delivered: number;
    total_clicked: number;
    total_failed: number;
    avg_ctr: number;
    attributed_registrations: number;
    attributed_deposits: number;
  };
  top_campaigns?: Array<{
    id: number;
    name: string;
    push_type: string;
    push_count: number;
    sent: number;
    clicked: number;
    ctr: number;
    registrations: number;
    deposits: number;
    conversion_rate: number;
  }>;
  conversions: ConversionStats;
  realtime?: {
    active_pushes: number;
    realtime_metrics?: {
      sent: number;
      delivered: number;
      clicked: number;
    };
    queue_size?: number;
  };
}

export interface AppStats {
  period: string;
  app_id: number;
  users?: {
    total_users: number;
    active_users: number;
    with_registration: number;
    with_deposit: number;
    by_os: Record<string, number>;
    by_locale: Record<string, number>;
    by_offer: Record<string, number>;
  };
  push_stats?: {
    total_sent: number;
    total_delivered: number;
    total_clicked: number;
    avg_ctr: number;
    attributed_registrations: number;
    attributed_deposits: number;
  };
}

export interface ConversionStats {
  registrations_total: number;
  registrations_from_push: number;
  deposits_total: number;
  deposits_from_push: number;
  registration_rate: number;
  deposit_rate: number;
}

export interface UserAnalytics {
  period: string;
  installs: number;
  registrations: number;
  deposits: number;
}

export interface PushPerformanceResponse {
  app_id: number;
  period: string;
  summary: {
    total_sent: number;
    total_delivered: number;
    total_clicked: number;
    avg_ctr: number;
  };
  by_type: Record<string, {
    count: number;
    sent: number;
    clicked: number;
    registrations: number;
    deposits: number;
    ctr: number;
  }>;
  top_performing: Array<{
    message_id: string;
    title: string;
    body: string;
    sent: number;
    clicked: number;
    ctr: number;
    conversions: number;
  }>;
}

export interface PushStatsResponse {
  message_id: string;
  statistics: {
    id: number;
    message_id: string;
    sent_count: number;
    delivered_count: number;
    clicked_count: number;
    failed_count: number;
    attributed_registrations: number;
    attributed_deposits: number;
    updated_at: string;
  };
  push_info?: {
    title: string;
    body: string;
    type: string;
    config_type: string;
    scheduled_at?: string;
    sent_at?: string;
    status: string;
  };
  metrics: {
    ctr: number;
    delivery_rate: number;
    failure_rate: number;
  };
}

export interface ExportStatsParams {
  app_id: number;
  type: 'push' | 'users' | 'conversions';
  period?: '7d' | '30d' | '90d';
}

export interface CompareRequest {
  campaign_ids?: number[];
  segment_ids?: number[];
  app_id?: number;
  period?: string;
}

export interface PushMessageGroup {
  group_key: string;
  title: string;
  body: string;
  push_type: PushType;
  count: number;
  message_ids: string[];
  total_sent: number;
  total_delivered: number;
  total_clicked: number;
  total_failed: number;
  total_target_users: number;
  last_sent_at: string | null;
  first_created_at: string;
  statuses: string[];
  segment_names: string[];
}

export interface PushHistoryGroupedItem {
  is_group: boolean;
  group?: PushMessageGroup;
  push?: PushMessage;
}

export interface PushHistoryGroupedResponse {
  items: PushHistoryGroupedItem[];
  total: number;
  limit: number;
  offset: number;
  grouped: boolean;
}

export interface SendTestPushRequest
  extends Omit<SendPushRequest, 'segment_id' | 'schedule_at'> {
  push_token: string;
}